import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { format, parseISO } from 'date-fns';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 11,
    fontFamily: "Helvetica",
    backgroundColor: "#F9F9F9",
    color: "#263238",
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 25,
  },
  logo: {
    width: 60,
    height: 60,
  },
  title: {
    fontSize: 20,
    textAlign: "right",
    color: "#1B5E20",
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  section: {
    padding: 12,
    border: "1pt solid #B0BEC5",
    borderRadius: 5,
    marginBottom: 18,
    backgroundColor: "#FFFFFF",
  },
  sectionTitle: {
    fontSize: 13,
    marginBottom: 10,
    borderBottom: "1pt solid #90A4AE",
    paddingBottom: 4,
    fontWeight: "bold",
    color: "#37474F",
  },
  field: {
    marginBottom: 6,
  },
  label: {
    fontWeight: "bold",
    color: "#455A64",
  },
  expenseRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  rowItem: {
    width: '15%',
    fontSize: 9,
  },
  rowItemWide: {
    width: '30%',
    fontSize: 9,
  },
  imageTitle: {
    fontSize: 13,
    marginBottom: 12,
    fontWeight: 'bold',
    color: '#37474F',
    textAlign: 'center',
  },
  imageGrid: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
  },
  invoiceImage: {
    width: 300,
    height: 220,
    objectFit: 'contain',
    border: "1pt solid #B0BEC5",
    borderRadius: 6,
    marginBottom: 14,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    fontSize: 9,
    textAlign: "center",
    width: "100%",
    color: "#90A4AE",
  },
});

const Field = ({ label, value }) => (
  <View style={styles.field}>
    <Text>
      <Text style={styles.label}>{label}: </Text>
      <Text>{value || 'N/A'}</Text>
    </Text>
  </View>
);

const CheckRequestPDF = ({ formData }) => {
  const formatDate = (dateStr) =>
    dateStr ? format(parseISO(dateStr), 'MM/dd/yyyy') : 'N/A';

  return (
    <Document>
      {/* Page 1: All text sections */}
      <Page size="A4" style={styles.page}>
        <View style={styles.headerContainer}>
          <Image src="/rciLogo.png" style={styles.logo} />
          <Text style={styles.title}>Check Request</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Supplier Information</Text>
          <Field label="Supplier #" value={formData.supplierNumber} />
          <Field label="Supplier Name" value={formData.supplierName} />
          <Field label="Address" value={formData.address} />
          <Field label="Date of Request" value={formatDate(formData.dateOfRequest)} />
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Invoice</Text>
          <Field label="Invoice #" value={formData.invoiceNumber} />
          <Field label="Invoice Date" value={formatDate(formData.invoiceDate)} />
          <Field label="Invoice Will Be Sent Later" value={formData.invoiceSentLater === 'yes' ? 'Yes' : 'No'} />
          <Field label="Purpose" value={formData.purpose} />
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Handling</Text>
          <Field label="Return Check To" value={formData.returnTo === 'other' ? `Other: ${formData.otherName}` : 'Requester'} />
          <Field label="Overnight Mail" value={formData.overnightMail ? 'Yes' : 'No'} />
          <Field label="Regular Mail" value={formData.regularMail ? 'Yes' : 'No'} />
          <Field label="Date Check Needed" value={formatDate(formData.dateNeeded)} />
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Expense Details</Text>
          {formData.expenseDetails?.map((item, index) => (
            <View style={styles.expenseRow} key={index}>
              <Text style={styles.rowItem}>{item.jobNumber || '-'}</Text>
              <Text style={styles.rowItem}>{item.glAcct || '-'}</Text>
              <Text style={styles.rowItem}>{item.costCode || '-'}</Text>
              <Text style={styles.rowItem}>{item.equipmentNumber || '-'}</Text>
              <Text style={styles.rowItem}>{item.amount || '-'}</Text>
              <Text style={styles.rowItemWide}>{item.description || '-'}</Text>
            </View>
          ))}
        </View>

        <View style={styles.section}>
          <Field label="Check Total" value={formData.checkTotal} />
          <Field label="Requested By" value={formData.requestedBy} />
          <Field label="Approved By" value={formData.approvedBy} />
        </View>

        <Text style={styles.footer}>Confidential — For Internal Use Only</Text>
      </Page>

      {/* Page 2: Images Only */}
      {formData.invoiceImageURLs?.length > 0 && (
        <Page size="A4" style={styles.page}>
          <Text style={styles.imageTitle}>Attached Invoice Images</Text>
          <View style={styles.imageGrid}>
            {formData.invoiceImageURLs.map((url, index) => (
              <Image key={index} src={url} style={styles.invoiceImage} wrap={false} />
            ))}
          </View>
          <Text style={styles.footer}>Confidential — For Internal Use Only</Text>
        </Page>
      )}
    </Document>
  );
};

export default CheckRequestPDF;
